import Image from "next/image";

import ProjectChips from "./ProjectChips";
import ResponsiveCopy from "../../ui/ResponsiveCopy";

// One client card: the product shot fills the frame and the story sits directly
// on top of it in the bottom-left corner — no panel, no box. Contrast comes
// from a gradient scrim weighted to that corner, so the rest of the photograph
// stays untouched.
//
// The caller owns the frame's size, aspect and corner radius: the desktop deck
// runs full-bleed with square corners, while the touch deck and the static
// fallback are rounded portrait cards. Everything here is relative to the frame.
// A client may carry an `imagePosition` class to steer that crop — see the
// clients table in PortfolioPreview.
//
// Purely presentational and hook-free, so all three deck variants can share it.
export default function DeckCard({ client, className = "", sizes = "100vw" }) {
  return (
    <article className={`relative overflow-hidden bg-neutral-900 ${className}`}>
      {client.image ? (
        <Image
          draggable={false}
          src={client.image}
          placeholder={client.image.blurDataURL ? "blur" : "empty"}
          alt={client.imageAlt}
          fill
          quality={client.id === "hatari" ? 90 : 75}
          sizes={client.id === "hatari" ? `(max-width: 1023px) 240vw, ${sizes}` : sizes}
          className={`object-cover ${client.imagePosition ?? ""}`}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <span className="text-xs uppercase tracking-[0.2em] text-white/25">
            {client.name}
          </span>
        </div>
      )}

      {/* Two scrims rather than one: a broad bottom fade to seat the card, plus
          a tighter corner wash under the copy. A single gradient dark enough to
          carry body text over a bright photo ends up dulling the whole shot. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-transparent"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(120%_90%_at_0%_100%,rgba(0,0,0,0.85)_0%,rgba(0,0,0,0.45)_35%,transparent_70%)]"
      />

      <div className="absolute inset-x-6 bottom-8 max-w-[620px] md:inset-x-10 lg:bottom-14 lg:left-14">
        {client.logo ? (
          <Image
            draggable={false}
            src={client.logo}
            alt={client.name}
            width={186}
            height={110}
            className="h-auto max-h-10 w-auto max-w-[160px] object-contain object-left drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)] lg:max-h-12 lg:max-w-[200px]"
          />
        ) : (
          <span className="text-xl font-semibold tracking-tight text-white">
            {client.name}
          </span>
        )}

        <h3 className="mt-5 text-lg font-bold leading-snug text-white sm:text-xl lg:text-2xl">
          <ResponsiveCopy desktop={client.title} mobile={client.titleMobile} />
        </h3>

        <p className="mt-5 line-clamp-4 text-sm leading-relaxed text-white/85 sm:text-base lg:line-clamp-none lg:text-lg">
          <ResponsiveCopy desktop={client.desc} mobile={client.descMobile} />
        </p>

        <ProjectChips badges={client.badges} tags={client.tags} />
      </div>
    </article>
  );
}
